/*
 * Copyright (c) Nexign, JSC, 2020-2021.
 */
/**
 * Page component for Labels Import
 *
 * @author Anton Panasenko
 * @author Yakov Gushchin
 * @version 1.11.0
 * @since 1.3.0
 */

import { FC } from 'react';

import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { EBasketType } from '../../../modules/app/appConstants';
import PageLocale from '../../../modules/internationalization/components/PageLocale';
import { TranslationFileNamesEnum } from '../../../modules/internationalization/constants';
import { makeSelectLabelsImportTranslation } from '../../../modules/internationalization/intSelectors';
import { ILabelsImportTranslation } from '../../../modules/internationalization/types/wizards/labelsImport';
import Wizard from '../../../modules/wizard/components/Wizard';
import { EWizardName } from '../../../modules/wizard/wizardConstants';
import { makeSelectWizardStepName } from '../../../modules/wizard/wizardSelectors';
import { TWizardStepNames } from '../../../modules/wizard/wizardTypes';
import { IRootReducer } from '../../../reducers/rootReducer';
import { labelsImportHandleNext } from '../labelsImportActions';
import { ELabelsImportLabelsValidationStatus, ELabelsImportSteps } from '../labelsImportConstants';
import { ILabelsImportLabelsValidationInfo } from '../labelsImportReducer';
import {
    makeSelectLabelsImportIsStepValid,
    makeSelectLabelsImportLabelsValidationInfo,
    makeSelectLabelsImportValidateError,
} from '../labelsImportSelectors';
import LabelsImportStepFile from './LabelsImportStepFile';
import LabelsImportStepFinal from './LabelsImportStepFinal';
import LabelsImportTitleElements from './LabelsImportTitleElements';

interface ILabelsImportProps {
    basketType: EBasketType;
}

export type TLabelsImportProps = ILabelsImportProps & IConnectedProps & IDispatchProps;

export const LabelsImport: FC<TLabelsImportProps> = (props) => {
    const renderStep = () => {
        switch (props.stepName) {
            case ELabelsImportSteps.File:
                return <LabelsImportStepFile/>
            case ELabelsImportSteps.Final:
                return <LabelsImportStepFinal/>
        }
    }

    const isDisableButtons = props.stepName === ELabelsImportSteps.Final &&
        props.labelsValidationInfo.status === ELabelsImportLabelsValidationStatus.RUNNING;

    return <PageLocale fileNames={[
        TranslationFileNamesEnum.common,
        TranslationFileNamesEnum.corporate,
        TranslationFileNamesEnum.labelsImport,
        TranslationFileNamesEnum.wizard,
    ]}>
        <Wizard
            basketType={props.basketType}
            isBackDisabled={isDisableButtons}
            isCancelDisabled={isDisableButtons}
            isNextDisabled={!props.isStepValid || isDisableButtons}
            isRequestsSticky={true}
            name={EWizardName.LABELS_IMPORT}
            titleElements={
                <LabelsImportTitleElements
                    stepName={props.stepName}
                    title={props.labelsImportTranslation.exportErrorsButton}/>
            }
            translationProps={{
                error: props.labelsImportTranslation.error,
                steps: props.labelsImportTranslation.steps,
                title: props.labelsImportTranslation.title,
            }}
            validateError={props.validateError}
            onWizardNextHandler={props.handleNext}
        >
            {renderStep()}
        </Wizard>
    </PageLocale>
}

interface IConnectedProps {
    isStepValid: boolean;
    labelsImportTranslation: ILabelsImportTranslation;
    labelsValidationInfo: ILabelsImportLabelsValidationInfo;
    stepName: TWizardStepNames;
    validateError: string;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    isStepValid: makeSelectLabelsImportIsStepValid(),
    labelsImportTranslation: makeSelectLabelsImportTranslation(),
    labelsValidationInfo: makeSelectLabelsImportLabelsValidationInfo(),
    stepName: makeSelectWizardStepName(),
    validateError: makeSelectLabelsImportValidateError(),
});

interface IDispatchProps {
    handleNext: () => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    handleNext: () => dispatch(labelsImportHandleNext()),
});

export default connect(mapStateToProps, mapDispatchToProps)(LabelsImport);
